import type {
  SignatureRequestSignerRole,
  SignatureRequestStatus,
} from '@/shared/api/resources';

/**
 * Rotulos da assinatura por provedor (P-MIN-4). Ver
 * docs/ADR/0062-assinatura-por-provedor.md.
 */

export const PERMISSAO_VER_PEDIDOS = 'assinaturas.pedido.view';
export const PERMISSAO_PEDIR_ASSINATURA = 'assinaturas.pedido.create';
export const PERMISSAO_CONFIGURAR_ASSINATURA = 'assinaturas.configuracao.update';
export const PERMISSAO_BAIXAR_ORIGINAL = 'apolices.documento_original.view';

export const situacaoDoPedido: Record<
  SignatureRequestStatus,
  { label: string; tone: 'info' | 'success' | 'neutral' | 'danger' | 'warning' }
> = {
  pendente: { label: 'Aguardando assinatura', tone: 'info' },
  assinado: { label: 'Assinado', tone: 'success' },
  recusado: { label: 'Recusado', tone: 'danger' },
  expirado: { label: 'Prazo vencido', tone: 'warning' },
  cancelado: { label: 'Cancelado', tone: 'neutral' },
  erro: { label: 'Erro no envio', tone: 'danger' },
  encerrado_incompleto: {
    label: 'Encerrado sem todas as assinaturas',
    tone: 'warning',
  },
};

export const papelDoSignatario: Record<SignatureRequestSignerRole, string> = {
  seguradora: 'Seguradora',
  tomador: 'Tomador',
  segurado: 'Segurado',
  corretor: 'Corretor',
  representante_legal: 'Representante legal',
  avalista: 'Avalista',
  testemunha: 'Testemunha',
};

export const situacaoDoSignatario: Record<string, string> = {
  pendente: 'Pendente',
  assinado: 'Assinou',
  recusado: 'Recusou',
};

export const origemDoEvento: Record<string, string> = {
  usuario: 'Pessoa',
  webhook: 'Retorno do provedor',
  consulta: 'Consulta ao provedor',
  sistema: 'Rotina',
};

export const tipoDoEvento: Record<string, string> = {
  pedido_criado: 'Pedido criado',
  enviado: 'Enviado ao provedor',
  falha_no_envio: 'Falha no envio',
  signatario_assinou: 'Signatario assinou',
  signatario_recusou: 'Signatario recusou',
  assinado: 'Documento assinado',
  recusado: 'Documento recusado',
  expirado: 'Prazo vencido',
  cancelado: 'Pedido cancelado',
  versao_assinada_guardada: 'Versao assinada guardada',
  falha_na_versao_assinada: 'Versao assinada nao guardada',
  lembrete_enviado: 'Lembrete enviado',
  consulta: 'Consulta ao provedor',
  evento_do_provedor: 'Aviso do provedor',
  retorno_recusado: 'Aviso sem efeito',
  envio_sem_confirmacao: 'Envio sem confirmacao do provedor',
  encerrado_incompleto: 'Encerrado sem todas as assinaturas',
};

/**
 * Motivo de o documento nao ser gerado de novo (nem cancelado) pelo pedido de assinatura
 * dele: com pedido pendente, os signatarios estao assinando aquele documento; com pedido
 * assinado, a versao assinada e a que vale. Vale para o documento da apolice e para a
 * minuta e a CCG da operacao (ADR 0079, R5-A03).
 */
export function motivoDaTravaDaAssinatura(
  situacao: string | null | undefined,
): string | undefined {
  if (situacao === 'pendente') {
    return 'Ha pedido de assinatura em andamento sobre este documento. Cancele o pedido antes de gerar de novo.';
  }
  if (situacao === 'assinado') {
    return 'O documento ja foi assinado no provedor: a versao assinada e a que vale.';
  }
  return undefined;
}

export const ambienteDoProvedor: Record<string, string> = {
  simulado: 'simulado, sem validade juridica',
  sandbox: 'sandbox, ambiente de teste do provedor',
  producao: 'producao',
};

export const movimentos = [
  { value: 'emissao', label: 'Emissao' },
  { value: 'endosso', label: 'Endosso' },
  { value: 'cancelamento', label: 'Cancelamento' },
  { value: 'renovacao', label: 'Renovacao' },
] as const;
